import { Component, OnInit } from "@angular/core"
import { Title } from "@angular/platform-browser"
import { NotificationsService } from "angular2-notifications"
import { Cookie } from "../models/cookie.model"
import { School } from "../models/school.model"
import { UserProfile } from "../models/userProfile.model"
import { CookiesService } from "../services/cookies.service"
import { UserProfileService } from "../services/user-profile.service"
import { UserService } from "../services/user.service"
import { GTMService } from "../services/gtm.service"
import { ModalService } from "../shared-components/modal/modal.service"
import { AddSchoolModalComponent } from "../shared-components/modals/add-school-modal/add-school-modal.component"
import { EduComponent } from "../utils/eduComponent"
import { getDRFErrorMessages } from "../utils/error-handling"
import { ChangePasswordModalComponent } from "../shared-components/modals/change-password-modal/change-password-modal.component"
import Utils from "../utils/utils"
import { PBSLM_TITLE, SUGGESTED_SCHOOL_ID } from "../models/constants"
import { LocalizationModalComponent } from "../shared-components/modals/localization-modal/localization-modal.component"
import { LocalizationService } from "../services/localization.service"
import { NotificationModalComponent } from "../shared-components/modals/notification-modal/notification-modal.component"

@Component({
    templateUrl: "./user-profile.component.html",
    styleUrls: ["./user-profile.component.scss"],
})
export class UserProfileComponent extends EduComponent implements OnInit {
    public userProfile: UserProfile
    public schools: any[] = []
    public selectedSchool: any
    public roles: any[] = []
    public selectedRole: any
    public grades: string[] = []
    public subjects: any[] = []
    public gradesCheckboxesModel: any[]
    public subjectsCheckboxModel: any[]
    public studentCalloutImage: string
    public suggestedSchool: any = {}
    public suggestNewSchoolModalRef: any
    public stateStandards: string = ""
    public localizationLogo: string = ""
    public googleClassImg: string = ""
    public zipCode: string = "None"
    public isProfileSuccessfullySaved: boolean = true

    constructor(
        private userProfileService: UserProfileService,
        private cookieService: CookiesService,
        private userService: UserService,
        private notificationService: NotificationsService,
        private gtmService: GTMService,
        private titleService: Title,
        private localizationService: LocalizationService,
        private modalService: ModalService
    ) {
        super()
        this.titleService.setTitle(`Profile | ${PBSLM_TITLE}`)
        this.gtmService.pushCleanPageView()
        this.studentCalloutImage = require("../../../../lmfrontend/src/assets/teacher-and-students.png").default
        this.googleClassImg = require("../../assets/google_classroom_x48_yellow_stroke_icon.png").default
    }

    public ngOnInit(): void {
        this.roles = this.userProfileService.getTeacherRoles()
        this.grades = this.userProfileService.getGrades()
        this.subjects = this.userProfileService.getSubjects()

        this.addSub(
            this.localizationService.localizeData.subscribe((data) => {
                this.stateStandards = data.stateStandards
                this.localizationLogo = data.getLogo()
            })
        )

        this.addSub(
            this.userProfileService.getUserProfile().subscribe((response) => {
                this.userProfile = new UserProfile(response)
                this.zipCode = this.userProfile.zipCode
                this.refreshAvailableSchools()

                if (this.userProfile.userRole) {
                    this.selectedRole = this.preselectSelectorValue(
                        this.roles,
                        this.userProfile.userRole,
                        "value"
                    )
                } else {
                    this.selectedRole = { id: null, value: "No role selected" }
                }

                this.gradesCheckboxesModel = this.buildGradesPanel(
                    this.userProfile.gradeRange,
                    this.grades
                )
                this.subjectsCheckboxModel = this.buildSubjectsPanel(
                    this.userProfile.preferredSubjects,
                    this.subjects
                )

                if (this.userService.shouldShowVirginiaModal) {
                    this.modalService.open(LocalizationModalComponent, {
                        data: {
                            virginiaLocalization: true,
                            shouldShowVirginiaModal: false,
                        },
                    })
                }
            })
        )
    }

    public refreshAvailableSchools(): void {
        this.addSub(
            this.userProfileService
                .getAvailableSchools(this.userProfile.zipCode)
                .subscribe((response) => {
                    this.schools = response.schools
                    this.schools.push({ id: null, name: "No school selected" })
                    this.selectedSchool = this.preselectSelectorValue(
                        this.schools,
                        this.userProfile.userSchool,
                        "id"
                    )
                })
        )
    }

    public updateProfile(): void {
        const nextUrl: Cookie = this.cookieService.getCookie("next")
        this.userProfile.gradeRange = this.generateGradesRange(this.gradesCheckboxesModel)
        this.userProfile.preferredSubjects = this.generatePreferredSubjects(
            this.subjectsCheckboxModel
        )

        if (this.selectedRole.id) {
            this.userProfile.userRole = this.selectedRole.value
        } else {
            this.userProfile.userRole = this.selectedRole.id
        }

        if (this.selectedSchool) {
            this.userProfile.userSchool = this.selectedSchool.id
        }

        this.addSub(
            this.userProfileService
                .updateUserProfile(this.userProfile.reverseToApiResponse())
                .subscribe(
                    (response) => {
                        this.isProfileSuccessfullySaved = true
                        if (nextUrl) {
                            this.cookieService.deleteCookie("next")
                            window.location.replace(nextUrl.value)
                        }
                    },
                    (error) => {
                        const errors = getDRFErrorMessages(error)
                        if (errors && errors.length > 0) {
                            errors.forEach((errorMsg) => {
                                this.isProfileSuccessfullySaved = false
                            })
                        } else {
                            this.isProfileSuccessfullySaved = false
                        }
                    }
                )
        )
    }

    // INFO:
    // > optionsArray: (The) array that keeps available options for selector.
    // > defaultSelection: (The) default option user had set.
    // > optionNameField: (The) field used to determine the value (name) of the option.
    public preselectSelectorValue(
        optionsArray: object[],
        defaultSelection: string | number,
        optionNameField: string
    ): any {
        return optionsArray.find((item) => item[optionNameField] === defaultSelection)
    }

    public changeCheckboxesModel(newValue, item, checkboxesModel): void {
        let checkbox = checkboxesModel.find((x) => x.label === item.label)
        if (checkbox && checkbox.checkValue !== undefined) {
            checkbox.checkValue = newValue
        }
    }

    public buildGradesPanel(preselectedGradesRange: string, defaultGrades: string[]): any[] {
        const checkedOptions = preselectedGradesRange.split(",")
        const defaultModel = defaultGrades.map((item) => {
            return { label: item, checkValue: false }
        })

        defaultModel.forEach((item) => {
            if (checkedOptions.find((el) => el === item.label)) {
                item.checkValue = true
            }
        })

        return defaultModel
    }

    public buildSubjectsPanel(preselectedSubjects: any[], defaultSubjects: any[]): any[] {
        const defaultModel = defaultSubjects.map((item) => {
            return { label: item.label, id: item.id, checkValue: false }
        })

        defaultModel.forEach((item) => {
            if (preselectedSubjects.find((el) => el === item.id)) {
                item.checkValue = true
            }
        })

        return defaultModel
    }

    private generateGradesRange(gradesCheckboxesModel: any[]): string {
        return gradesCheckboxesModel
            .map((item) => {
                if (item.checkValue === true) {
                    return item.label
                }
            })
            .filter((item) => item != null)
            .toString()
    }

    private generatePreferredSubjects(subjectsCheckboxModel: any[]): number[] {
        return subjectsCheckboxModel
            .map((item) => {
                if (item.checkValue === true) {
                    return item.id
                }
            })
            .filter((item) => item != null)
    }

    public getAccountEditLink(): string {
        return this.userService.userAccountEditUrl
    }

    public openAddSchoolModal(): void {
        this.suggestNewSchoolModalRef = this.modalService.open(AddSchoolModalComponent, {
            size: "lg",
        })
        this.suggestNewSchoolModalRef.componentInstance.suggestedSchool = this.suggestedSchool
        this.suggestNewSchoolModalRef.componentInstance.states = this.userProfileService.getStates()
        this.suggestNewSchoolModalRef.componentInstance.handler.subscribe((response) => {
            this.suggestSchool(response)
        })
    }

    public suggestSchool(response: any): void {
        const suggestedSchool = new School(response)

        if (suggestedSchool.name !== "" && suggestedSchool.zipCode !== "") {
            this.addSub(
                this.userProfileService
                    .postNewSchool(suggestedSchool.generateApiPayload(), {
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                        },
                    })
                    .subscribe(
                        (response) => {
                            const informationModal = this.modalService.open(
                                NotificationModalComponent
                            )
                            informationModal.componentInstance.message =
                                "Your school was successfully added to your profile."
                            this.userProfile.userSchool = SUGGESTED_SCHOOL_ID
                            this.refreshAvailableSchools()
                        },
                        (error) => {
                            const errors = getDRFErrorMessages(error, ["user_role"])
                            if (errors && errors.length > 0) {
                                errors.forEach((errorMsg) => {
                                    this.notificationService.error(
                                        "Error",
                                        errorMsg,
                                        Utils.alertConfiguration
                                    )
                                })
                            } else {
                                this.notificationService.error(
                                    "Error",
                                    "Please try again later.",
                                    Utils.alertConfiguration
                                )
                            }
                        }
                    )
            )
        }
    }

    public clickChangePassword() {
        this.updateProfile()
        this.modalService.open(ChangePasswordModalComponent)
    }

    public openLocalizationChangeModal() {
        this.updateProfile()
        this.modalService.open(LocalizationModalComponent, {
            size: "lg",
            windowClass: "change-station-modal",
        })
    }

    public openProfileUpdateConfirmationModal() {
        this.modalService.open(NotificationModalComponent, {
            data: {
                message: this.isProfileSuccessfullySaved
                    ? "Your profile information has been saved."
                    : "Something unexpected happened and the profile changes couldn't be saved. Please try again!",
            },
        })
    }
}
